import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { MCPConfig } from "@/store/zustand/mcpServersStore";
import { useMCPServers } from "@/hooks/useMCPServers";
import { useState } from "react";
import { Loader2, Trash2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useMCPConnection } from "./hooks/useMCPConnection";
import { Tooltip } from "react-tooltip";

interface MCPCardProps {
  config: MCPConfig;
  isConnected?: boolean;
  isDisconnected?: boolean;
}

export default function MCPCard({
  config,
  isConnected = false,
  isDisconnected = false,
}: MCPCardProps) {
  const { servers, enableServer, disableServer, deleteServer } =
    useMCPServers();

  const { isConnecting, connectToServer } = useMCPConnection();
  const [isDeleting, setIsDeleting] = useState(false);

  const connection = servers.find((s) => s.server_url === config.serverUrl);

  const handleConnect = async () => {
    await connectToServer(
      config.serverUrl,
      config.displayName,
      config.transportType || "http",
    );
  };

  const handleToggle = async (checked: boolean) => {
    if (!connection) return;

    try {
      if (checked) {
        await enableServer(connection.id);
      } else {
        await disableServer(connection.id);
      }
    } catch (err: any) {
      console.error(
        `Error ${checked ? "enabling" : "disabling"} ${config.displayName}:`,
        err,
      );
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        `Failed to ${checked ? "enable" : "disable"}`;
      toast.error(
        `Failed to ${checked ? "enable" : "disable"} ${config.displayName}`,
        {
          description: errorMessage,
        },
      );
    }
  };

  const handleDelete = async () => {
    if (!connection) return;

    setIsDeleting(true);

    try {
      await deleteServer(connection.id);
      toast.success(`Disconnected ${config.displayName}`);
      setIsDeleting(false);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to disconnect";
      toast.error(`Failed to disconnect ${config.displayName}`, {
        description: errorMessage,
      });
      setIsDeleting(false);
    }
  };

  const renderDeleteButton = (title: string) => (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleDelete}
      disabled={isDeleting}
      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-transparent"
      title={title}
    >
      {isDeleting ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Trash2 className="h-3.5 w-3.5" />
      )}
    </Button>
  );

  const renderConnectButton = (label: string) => (
    <Button
      onClick={handleConnect}
      disabled={isConnecting}
      variant="primary"
      size="sm"
      className="h-7 text-xs px-3"
    >
      {isConnecting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
      {label}
    </Button>
  );

  return (
    <div className="border border-border rounded-lg p-4 hover:border-primary-0/50 transition-colors bg-background h-full flex flex-col">
      {/* Header - Icon and Title */}
      <div className="flex items-center gap-2 mb-3">
        <img
          src={config.icon}
          alt={config.displayName}
          className="h-5 w-5 shrink-0"
        />
        <h3 className="text-sm font-semibold text-foreground truncate flex-1">
          {config.displayName}
        </h3>
        {isConnected && connection?.enabled && (
          <div
            className="h-2 w-2 rounded-full bg-green-500 shrink-0"
            data-tooltip-id={`status-${config.id}`}
          />
        )}
        {isDisconnected && (
          <div
            className="h-2 w-2 rounded-full bg-orange-500 shrink-0"
            data-tooltip-id={`status-${config.id}`}
          />
        )}
        {config.instructions && config.instructions.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-muted-foreground hover:text-foreground hover:bg-transparent"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-foreground">
                  Setup Instructions
                </h4>
                <ol className="list-decimal list-inside space-y-1.5 text-xs text-muted-foreground">
                  {config.instructions.map((instruction, index) => (
                    <li key={index} className="leading-relaxed">
                      {instruction}
                    </li>
                  ))}
                </ol>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <p className="text-xs text-muted-foreground line-clamp-3 mb-3 leading-relaxed">
        {config.description}
      </p>

      <div className="flex-1 min-h-[8px]" />

      <div className="border-t border-border -mx-4 mb-2.5" />

      <div className="flex items-center justify-between h-7">
        <div className="flex items-center gap-2">
          {config.docsUrl && (
            <a
              href={config.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors hover:underline"
            >
              Learn More
            </a>
          )}
        </div>

        <div className="flex items-center gap-1">
          {!isConnected && !isDisconnected && renderConnectButton("Connect")}

          {isDisconnected && (
            <>
              {renderDeleteButton("Remove connection")}
              {renderConnectButton("Reconnect")}
            </>
          )}

          {isConnected && (
            <>
              {renderDeleteButton("Disconnect and remove")}
              <Switch
                checked={connection?.enabled || false}
                onCheckedChange={handleToggle}
                className="scale-[0.7]"
              />
            </>
          )}
        </div>
      </div>

      {/* Status tooltip */}
      {(isConnected || isDisconnected) && (
        <Tooltip
          id={`status-${config.id}`}
          className="z-200"
          style={{
            backgroundColor: "black",
            color: "white",
            fontSize: "0.75rem",
            padding: "4px 8px",
            borderRadius: "4px",
          }}
          opacity={1}
          arrowColor="transparent"
        >
          {isConnected && connection?.enabled && "Connected and active"}
          {isDisconnected && "Connection lost"}
        </Tooltip>
      )}
    </div>
  );
}
